'use client';

import { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { createClient } from '@/lib/supabase/client';

export type UserRole = 'admin' | 'clinician' | 'patient' | 'researcher';

export interface UserProfile {
  id: string;
  email: string;
  fullName: string;
  role: UserRole;
  department?: string;
  licenseNumber?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  customUserId?: string;
  displayUsername?: string;
}

interface AuthContextType {
  user: any;
  session: any;
  loading: boolean;
  profile: UserProfile | null;
  role: UserRole | null;
  isAdmin: boolean;
  isClinician: boolean;
  isPatient: boolean;
  signUp: (email: string, password: string, metadata?: any) => Promise<any>;
  signIn: (email: string, password: string) => Promise<any>;
  signOut: () => Promise<void>;
  getCurrentUser: () => Promise<any>;
  isEmailVerified: () => boolean;
  getUserProfile: () => Promise<UserProfile | null>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({} as AuthContextType);

// ── Signup cooldown guard — prevents repeated signUp calls hitting Supabase rate limit ──
const signupCooldowns = new Map<string, number>();
const SIGNUP_COOLDOWN_MS = 60 * 1000; // 60 seconds between signup attempts per email

function checkSignupCooldown(email: string): { blocked: boolean; waitSeconds?: number } {
  const lastAttempt = signupCooldowns.get(email);
  if (!lastAttempt) return { blocked: false };
  const elapsed = Date.now() - lastAttempt;
  if (elapsed < SIGNUP_COOLDOWN_MS) {
    return { blocked: true, waitSeconds: Math.ceil((SIGNUP_COOLDOWN_MS - elapsed) / 1000) };
  }
  return { blocked: false };
}

function recordSignupAttempt(email: string) {
  signupCooldowns.set(email, Date.now());
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

function mapProfile(data: any): UserProfile {
  return {
    id: data.id,
    email: data.email,
    fullName: data.full_name,
    role: data.role as UserRole,
    department: data.department ?? undefined,
    licenseNumber: data.license_number ?? undefined,
    isActive: data.is_active,
    createdAt: data.created_at,
    updatedAt: data.updated_at,
    customUserId: data.custom_user_id ?? undefined,
    displayUsername: data.display_username ?? undefined,
  };
}

// Module-level deduplication: only one getUser call in-flight at a time
let _sessionPromise: Promise<any> | null = null;

function getSessionOnce(supabase: ReturnType<typeof createClient>) {
  if (_sessionPromise) return _sessionPromise;
  _sessionPromise = supabase.auth.getUser().finally(() => {
    // Clear after a short window so future explicit refreshes still work
    setTimeout(() => { _sessionPromise = null; }, 1000);
  });
  return _sessionPromise;
}

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<any>(null);
  const [session, setSession] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  // Stabilize the supabase client in a ref so it never changes between renders.
  // Previously `createClient()` was called in the component body, which meant
  // every re-render produced a new variable reference — even though the underlying
  // singleton was the same. This caused fetchProfile's useCallback to be recreated
  // on every render (supabase was in its dep array), which in turn caused the
  // useEffect to re-run and fire duplicate auth requests.
  const supabaseRef = useRef(createClient());
  const supabase = supabaseRef.current;

  // Track whether initial session load is done so onAuthStateChange
  // doesn't fire a redundant profile fetch for the same session
  const initialLoadDone = useRef(false);
  const lastSessionId = useRef<string | null>(null);

  const fetchProfile = useCallback(async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', userId)
        .single();
      if (error || !data) return null;
      return mapProfile(data);
    } catch {
      return null;
    }
  // supabase is a stable ref — intentionally omitted from deps
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Retry getUser with exponential backoff when rate-limited (429)
  const getSessionWithRetry = useCallback(async (attempt = 0): Promise<any> => {
    try {
      const { data: { user }, error } = await getSessionOnce(supabase);
      if (error) {
        const msg = error.message?.toLowerCase() ?? '';
        const status = (error as any).status;
        const isRateLimit = status === 429 || msg.includes('rate limit');

        if (isRateLimit && attempt < 3) {
          // Exponential backoff: 3s, 6s, 12s (longer to respect rate limit window)
          const delay = Math.pow(2, attempt + 1) * 1500;
          await new Promise((resolve) => setTimeout(resolve, delay));
          _sessionPromise = null; // force a fresh call after backoff
          return getSessionWithRetry(attempt + 1);
        }

        if (isRateLimit) {
          return { user: null, rateLimited: true };
        }

        if (msg.includes('refresh token') || msg.includes('invalid') || msg.includes('not found')) {
          // Clear stale tokens from storage so Supabase doesn't keep retrying
          // with an invalid refresh token on subsequent page loads
          try {
            Object.keys(localStorage)
              .filter((k) => k.includes('supabase') || k.includes('sb-'))
              .forEach((k) => localStorage.removeItem(k));
          } catch { /* ignore */ }
          try {
            document.cookie.split(';').forEach((c) => {
              const name = c.trim().split('=')[0];
              if (name.includes('supabase') || name.includes('sb-')) {
                document.cookie = `${name}=; Path=/; Max-Age=0; SameSite=None; Secure`;
              }
            });
          } catch { /* ignore */ }
          await supabase.auth.signOut();
          return { user: null };
        }

        return { user: null };
      }
      return { user };
    } catch (err: any) {
      const msg = (err?.message ?? '').toLowerCase();
      // "Failed to fetch" = network error — clear stale tokens so the SDK
      // does not keep firing background refresh attempts with invalid tokens.
      if (msg.includes('failed to fetch') || msg.includes('network') || msg.includes('fetch')) {
        try {
          Object.keys(localStorage)
            .filter((k) => k.includes('supabase') || k.includes('sb-'))
            .forEach((k) => localStorage.removeItem(k));
        } catch { /* ignore */ }
        try {
          document.cookie.split(';').forEach((c) => {
            const name = c.trim().split('=')[0];
            if (name.includes('supabase') || name.includes('sb-')) {
              document.cookie = `${name}=; Path=/; Max-Age=0; SameSite=None; Secure`;
            }
          });
        } catch { /* ignore */ }
        // Do NOT call supabase.auth.signOut() here — it also makes a network
        // request and will throw another "Failed to fetch" error.
      }
      return { user: null };
    }
  }, []);

  useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        const result = await getSessionWithRetry();
        if (!mounted) return;

        const authUser = result?.user ?? null;
        setUser(authUser);
        lastSessionId.current = authUser?.id ?? null;

        if (authUser) {
          // Also get the session for token storage
          const { data: { session: sess } } = await supabase.auth.getSession();
          setSession(sess);
          const p = await fetchProfile(authUser.id);
          if (mounted) setProfile(p);
        } else {
          setSession(null);
        }
      } catch (err: any) {
        if (!mounted) return;
        const msg = (err?.message ?? '').toLowerCase();
        if (msg.includes('failed to fetch') || msg.includes('network')) {
          // Clear stale tokens directly — do NOT call signOut() over the network
          try {
            Object.keys(localStorage)
              .filter((k) => k.includes('supabase') || k.includes('sb-'))
              .forEach((k) => localStorage.removeItem(k));
          } catch { /* ignore */ }
        }
      } finally {
        if (mounted) {
          initialLoadDone.current = true;
          setLoading(false);
        }
      }
    })();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (_event, newSession) => {
      if (!mounted) return;

      const newUserId = newSession?.user?.id ?? null;

      // Skip if this is just the initial INITIAL_SESSION event echoing what
      // getUser already returned — avoids a duplicate profile fetch and
      // an extra auth request that triggers the rate limit.
      if (
        initialLoadDone.current &&
        newUserId === lastSessionId.current &&
        _event === 'INITIAL_SESSION'
      ) {
        return;
      }

      // TOKEN_REFRESHED only updates the session tokens — the user identity
      // hasn't changed, so skip the profile fetch to avoid an extra DB call.
      if (_event === 'TOKEN_REFRESHED' && newUserId === lastSessionId.current) {
        setSession(newSession);
        return;
      }

      setSession(newSession);
      setUser(newSession?.user ?? null);
      lastSessionId.current = newUserId;

      if (newSession?.user) {
        try {
          const p = await fetchProfile(newSession.user.id);
          if (mounted) setProfile(p);
        } catch { /* ignore profile fetch errors */ }
      } else {
        setProfile(null);
      }

      if (mounted) setLoading(false);
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const signUp = async (email: string, password: string, metadata: any = {}) => {
    const normalizedEmail = email.trim().toLowerCase();

    // Guard: prevent repeated signUp calls for same email hitting Supabase rate limit
    const cd = checkSignupCooldown(normalizedEmail);
    if (cd.blocked) {
      throw new Error(
        `Please wait ${cd.waitSeconds} second${cd.waitSeconds !== 1 ? 's' : ''} before trying to register again.`
      );
    }

    // NOTE: Do NOT record the attempt here — only record it when Supabase actually
    // returns a 429, so that transient failures (network errors, server errors, etc.)
    // don't falsely lock out the user for 60 seconds on their next legitimate attempt.

    const { data, error } = await supabase.auth.signUp({
      email: normalizedEmail,
      password,
      options: {
        data: {
          full_name: metadata?.fullName || metadata?.full_name || '',
          role: metadata?.role || 'patient',
          department: metadata?.department || '',
          license_number: metadata?.licenseNumber || metadata?.license_number || '',
        },
        emailRedirectTo: `${window.location.origin}/auth/callback`,
      },
    });
    if (error) {
      const msg = error.message?.toLowerCase() ?? '';
      if ((error as any).status === 429 || msg.includes('rate limit') || msg.includes('too many')) {
        // Only record the cooldown when Supabase itself is rate-limiting us
        recordSignupAttempt(normalizedEmail);
        throw new Error(
          'Registration limit reached. Please wait 60 seconds before trying again, or contact support if this persists.'
        );
      }
      throw error;
    }
    return data;
  };

  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) {
      const msg = error.message?.toLowerCase() ?? '';
      if ((error as any).status === 429 || msg.includes('rate limit')) {
        throw new Error('Too many requests. Please wait a moment and try again.');
      }
      throw error;
    }
    return data;
  };

  const signOut = async () => {
    try {
      await supabase.auth.signOut();
    } catch {
      // ignore signOut errors (e.g. already signed out)
    } finally {
      setUser(null);
      setSession(null);
      setProfile(null);
      lastSessionId.current = null;
    }
  };

  const getCurrentUser = async () => {
    // Use getUser() to authenticate via the Supabase Auth server for security.
    // This prevents using potentially unverified data from cookies/storage.
    const { data: { user: verifiedUser } } = await supabase.auth.getUser();
    return verifiedUser;
  };

  const isEmailVerified = () => user?.email_confirmed_at !== null;

  const getUserProfile = async (): Promise<UserProfile | null> => {
    if (!user) return null;
    const p = await fetchProfile(user.id);
    setProfile(p);
    return p;
  };

  const refreshProfile = async () => {
    if (!user) return;
    const p = await fetchProfile(user.id);
    setProfile(p);
  };

  const role = profile?.role ?? null;
  const isAdmin = role === 'admin';
  const isClinician = role === 'clinician';
  const isPatient = role === 'patient';

  const value: AuthContextType = {
    user,
    session,
    loading,
    profile,
    role,
    isAdmin,
    isClinician,
    isPatient,
    signUp,
    signIn,
    signOut,
    getCurrentUser,
    isEmailVerified,
    getUserProfile,
    refreshProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
